import { useEffect, useState } from "react";
import { Loader2, Mail, Save, Send, Plus, X, Users } from "lucide-react";
import { toast } from "sonner";
import { api, apiError } from "@/lib/api";
import { fmtDateTime, rupiah } from "@/lib/format";
import { useAuth } from "@/context/AuthContext";

export function ArWeeklySettings() {
  const { hasPerm } = useAuth();
  const canEdit = hasPerm("invoices:create");
  const [s, setS] = useState(null);
  const [emails, setEmails] = useState([]);
  const [draft, setDraft] = useState("");
  const [busy, setBusy] = useState(null);

  const apply = (d) => { setS(d); setEmails(d.extra_emails || []); };
  useEffect(() => { api.get("/finance/ar-weekly").then((r) => apply(r.data.data)).catch((e) => toast.error(apiError(e))); }, []);

  const addEmail = () => {
    const e = draft.trim().toLowerCase();
    if (!e) return;
    if (!/^[^@\s]+@[^@\s]+\.[a-z]{2,}$/.test(e)) { toast.error("Format email tidak valid"); return; }
    if (!emails.includes(e)) setEmails([...emails, e]);
    setDraft("");
  };
  const save = async (enabled = s.enabled, list = emails) => {
    setBusy("save");
    try { const r = await api.put("/finance/ar-weekly", { enabled, extra_emails: list }); apply(r.data.data); toast.success("Pengaturan laporan AR mingguan disimpan"); }
    catch (e) { toast.error(apiError(e)); } finally { setBusy(null); }
  };
  const sendNow = async () => {
    if (!window.confirm(`Kirim Laporan AR sekarang ke ${s.recipients.length} penerima?`)) return;
    setBusy("send");
    try {
      const r = await api.post("/finance/ar-weekly/send-now");
      const d = r.data.data;
      d.failed.length ? toast.warning(`Terkirim ${d.sent}, gagal ${d.failed.length}: ${d.failed.map((f) => f.email).join(", ")}`)
        : toast.success(`Laporan AR terkirim ke ${d.sent} penerima`);
      setS((x) => ({ ...x, last_run_at: d.at, last_result: d }));
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(null); }
  };

  if (!s) return <div className="panel"><div className="rj-loading"><Loader2 className="spin" /> Memuat…</div></div>;
  const dirty = JSON.stringify(emails) !== JSON.stringify(s.extra_emails || []);
  const lr = s.last_result;
  return (
    <div className="panel reminder-panel" data-testid="ar-weekly-panel">
      <div className="panel-head"><div><h3><Mail size={16} style={{ verticalAlign: -3 }} /> Laporan AR Mingguan via Email</h3>
        <p>{s.schedule}: ringkasan piutang per customer + tombol unduh PDF & Excel (tautan aman, berlaku 7 hari).</p></div></div>
      <div className="reminder-row">
        <div><strong>Kirim otomatis setiap Senin</strong><small>{s.enabled ? "Aktif" : "Nonaktif"}</small></div>
        <button className={`rj-switch ${s.enabled ? "on" : ""}`} onClick={() => canEdit && save(!s.enabled, emails)}
          disabled={!canEdit || !!busy} data-testid="ar-weekly-toggle" aria-label="Aktifkan laporan mingguan"><i /></button>
      </div>
      <div className="ar-weekly-recipients" data-testid="ar-weekly-recipients">
        <strong><Users size={14} /> Penerima ({s.recipients.length})</strong>
        <small className="fin-muted">Otomatis: pengguna aktif dengan hak akses "Terima email Laporan AR mingguan" (default role Finance Manager & Finance) + email khusus di bawah.</small>
        <div className="audit-chips">
          {s.recipients.filter((r) => r.source === "role").map((r) => (
            <span key={r.email} className="audit-chip add" title={r.email} data-testid={`ar-weekly-recipient-${r.email}`}>{r.name || r.email} · {r.role}</span>
          ))}
          {s.recipients.filter((r) => r.source === "role").length === 0 && <span className="fin-muted">Belum ada pengguna Finance Manager / Finance aktif.</span>}
        </div>
      </div>
      <div className="rj-field">Email khusus Manager Finance / penerima tambahan
        <div className="email-chip-input">
          {emails.map((e) => (
            <span key={e} className="email-chip" data-testid={`ar-weekly-extra-${e}`}>{e}
              {canEdit && <button type="button" onClick={() => setEmails(emails.filter((x) => x !== e))} aria-label="Hapus"><X size={12} /></button>}</span>
          ))}
          {canEdit && <input value={draft} onChange={(e) => setDraft(e.target.value)} placeholder="manager.finance@perusahaan.com"
            onKeyDown={(e) => { if (e.key === "Enter" || e.key === ",") { e.preventDefault(); addEmail(); } }} data-testid="ar-weekly-email-input" />}
          {canEdit && <button type="button" className="rj-btn rj-btn-ghost rj-btn-sm" onClick={addEmail} data-testid="ar-weekly-email-add"><Plus size={13} /> Tambah</button>}
        </div>
      </div>
      {canEdit && (
        <div className="reminder-actions">
          <button className="rj-btn rj-btn-green" onClick={() => save()} disabled={!!busy || !dirty} data-testid="ar-weekly-save-btn">
            {busy === "save" ? <Loader2 size={16} className="spin" /> : <><Save size={16} /> Simpan</>}
          </button>
          <button className="rj-btn rj-btn-ghost" onClick={sendNow} disabled={!!busy || dirty || s.recipients.length === 0} data-testid="ar-weekly-send-now-btn"
            title={dirty ? "Simpan perubahan dulu" : "Kirim laporan sekarang"}>
            {busy === "send" ? <Loader2 size={16} className="spin" /> : <><Send size={16} /> Kirim sekarang</>}
          </button>
        </div>
      )}
      {lr && (
        <p className="fin-muted" style={{ marginTop: 10 }} data-testid="ar-weekly-last-run">
          Terakhir dikirim {fmtDateTime(s.last_run_at)} ({lr.actor === "cron" ? "otomatis" : lr.actor}) · {lr.sent}/{lr.recipients} penerima
          · sisa piutang {rupiah(lr.outstanding)}{lr.failed?.length ? ` · gagal: ${lr.failed.map((f) => f.email).join(", ")}` : ""}
        </p>
      )}
    </div>
  );
}
